import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { API_BASE_URL as baseUri } from "config/env";
import {
    MarkPatchNotesAsSeenResponse,
    PatchNoteRelease,
    patchNotesInitialValue,
} from "./patch-notes-interfaces";

// The 'Slice/' prefix is load-bearing: store.ts routes only actions starting with it through
// TokenRefreshMiddleware, so a different prefix silently opts these calls out of automatic
// token refresh on a 401. No error, just no refresh.

/**
 * The language the notes are fetched in is always passed explicitly (i18n.language at the call
 * site), never left to the server. The server's own fallback - stored user culture, then the
 * corporation default, then English - only applies when this is absent, and the stored culture
 * is not rewritten when the user switches language in the UI: without the parameter the notes
 * would keep arriving in the previously saved language while the rest of the screen had already
 * switched. The language is recorded in the slice so a switch can be detected and refetched.
 */
const languageQuery = (language: string) => language ? `?language=${encodeURIComponent(language)}` : '';

/**
 * `current` returns 200 with a null body when nothing is published yet, so the response is
 * typed as nullable rather than treating "no releases" as an error.
 */
export const GetCurrentPatchNotes = createAsyncThunk<PatchNoteRelease | null, string, { rejectValue: RejectedValue }>(
    'Slice/PatchNotesSlice/GetCurrentPatchNotes',
    async (language, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/patch-notes/current' + languageQuery(language), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const text = await response.text();
            return text ? (JSON.parse(text) as PatchNoteRelease) : null;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetAllPatchNotes = createAsyncThunk<PatchNoteRelease[], string, { rejectValue: RejectedValue }>(
    'Slice/PatchNotesSlice/GetAllPatchNotes',
    async (language, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/patch-notes/all' + languageQuery(language), {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PatchNoteRelease[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

/**
 * Records that the user acknowledged a release. The version is sent explicitly so a stale tab
 * cannot acknowledge a newer release it never displayed; the server also refuses to move the
 * stored value backwards.
 */
export const MarkPatchNotesAsSeen = createAsyncThunk<MarkPatchNotesAsSeenResponse, string | null, { rejectValue: RejectedValue }>(
    'Slice/PatchNotesSlice/MarkPatchNotesAsSeen',
    async (version, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/patch-notes/mark-seen', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ version }),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as MarkPatchNotesAsSeenResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const PatchNotesSlice = createSlice({
    name: 'patchNotesSlice',
    initialState: patchNotesInitialValue,
    reducers: {
        openWhatsNewModal: (state) => {
            state.modalOpen = true;
            // Re-arm the acknowledgement. Whether the reopen button is offered afterwards is
            // decided by how the user closes it *this* time — otherwise one "Got it" suppresses
            // the button for the rest of the browser session, and every later set-aside is
            // silently overruled by that stale flag. This slice is persisted to sessionStorage
            // (store.ts blacklists only authentificationSlice and unsavedChangesSlice), so the
            // flag outlives a page reload and there is nothing else that would ever clear it.
            state.acknowledged = false;
        },
        /**
         * "Set aside" — X or backdrop click. The floating reopen button stays available.
         *
         * Closing is separate from persisting: the caller decides whether to also dispatch
         * MarkPatchNotesAsSeen. Failing to persist the acknowledgement must not keep the user
         * trapped behind the modal.
         */
        closeWhatsNewModal: (state) => {
            state.modalOpen = false;
            state.dismissedThisSession = true;
        },
        /**
         * "Done with it" — "Got it", or leaving for the full history page. Same close, but the
         * floating reopen button stays hidden for the rest of the session; offering to reopen
         * what the user just confirmed reading is noise. The history page (nav entry, or "View
         * all patch notes") remains the way back in.
         */
        acknowledgeWhatsNew: (state) => {
            state.modalOpen = false;
            state.dismissedThisSession = true;
            state.acknowledged = true;
        },
        resetPatchNotesSlice: () => patchNotesInitialValue,
    },
    extraReducers(builder) {
        builder.addCase(GetCurrentPatchNotes.pending, (state) => {
            state.isLoadingCurrent = true;
            state.currentError = '';
        })
            .addCase(GetCurrentPatchNotes.fulfilled, (state, action) => {
                state.isLoadingCurrent = false;
                state.current = action.payload;
                state.currentLanguage = action.meta.arg;
            })
            .addCase(GetCurrentPatchNotes.rejected, (state, action) => {
                state.isLoadingCurrent = false;
                state.currentError = action.payload?.message ?? 'Error loading patch notes';
                // Recorded on failure too, deliberately: the fetch effects refetch whenever the
                // stored language differs from the one on screen, so leaving it at the previous
                // value would spin that effect forever against an endpoint that is failing.
                state.currentLanguage = action.meta.arg;
            })

            .addCase(GetAllPatchNotes.pending, (state) => {
                state.isLoadingAll = true;
                state.allError = '';
            })
            .addCase(GetAllPatchNotes.fulfilled, (state, action) => {
                state.isLoadingAll = false;
                state.all = action.payload;
            })
            .addCase(GetAllPatchNotes.rejected, (state, action) => {
                state.isLoadingAll = false;
                state.allError = action.payload?.message ?? 'Error loading patch notes';
            })

            // Deliberately no state change on mark-seen: the modal is already closed by
            // closeWhatsNewModal, and a failed write only means the user sees the release once
            // more on the next login. Nothing to surface.
            .addCase(MarkPatchNotesAsSeen.rejected, (state) => {
                state.modalOpen = false;
            })
    }
});

export const { openWhatsNewModal, closeWhatsNewModal, acknowledgeWhatsNew, resetPatchNotesSlice } = PatchNotesSlice.actions;
export default PatchNotesSlice.reducer;
